import React, { useState } from 'react';

import { getObjectSumValues } from 'utilitys/helpers/general';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useVersionStore } from 'store/version.store';

import { useMutationBulkRollbackAction } from 'api/mutations/bulkRollbackAction.mutation';
import { ICountersDataResponse } from 'api/queries/battleCards.query';

import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import { ConfirmSaveModal } from 'components/common';

import classes from './bulkRollbackAction.module.scss';

interface Props {
    isShowModal: boolean;
    handleModalClose: () => void;
    cardsCounters?: Record<keyof ICountersDataResponse, number>;
    setIsBulkLoading: React.Dispatch<React.SetStateAction<boolean>>;
    searchedFilterData: any;
    bulkRollbackUniqIdNotification: number;
}

export const BulkRollbackAction = ({
    isShowModal,
    handleModalClose,
    cardsCounters,
    setIsBulkLoading,
    searchedFilterData,
    bulkRollbackUniqIdNotification,
}: Props) => {
    const [confirmModal, setConfirmModal] = useState<boolean>(false);
    const { handleSnackBar } = useCustomSnackBar();
    const { mutate } = useMutationBulkRollbackAction();
    const version = useVersionStore((state) => state.version);
    const { sendEventWithDimensions } = useEventWithDimensions();

    const handleConfirmModal = () => {
        if (getObjectSumValues(cardsCounters) === 0) {
            handleSnackBar('You cannot run a bulk rollback on zero activities. Update your filter.', 'warning');
        } else {
            setConfirmModal(true);
        }
    };

    const handleRollbackAction = () => {
        if (version) {
            const params = {
                ...searchedFilterData,
                uniqIdNotification: bulkRollbackUniqIdNotification,
                versionId: version.id,
            };

            mutate(params, {
                onSuccess: () => {
                    setIsBulkLoading(true);
                    sendEventWithDimensions({
                        category: 'Battlecards',
                        action: 'Bulk Rollback',
                        label: `filter: ${JSON.stringify(params.filter)}`,
                    });
                },
                onError: (error) => {
                    const message = 'Bulk request Failed';
                    DEFAULT_ERROR({ error, message, handleError: handleSnackBar });
                },
                onSettled: () => {
                    closeModal();
                },
            });
        }
    };

    const closeModal = () => {
        handleModalClose();
        setConfirmModal(false);
    };

    return (
        <>
            <Modal
                open={isShowModal}
                onClose={closeModal}
                title={<>{executionConfig.battlecards.title} - Bulk Rollback Status to Submitted</>}
                subtitle={
                    <>
                        Rollback status for{' '}
                        <span className={classes.bold}>{getObjectSumValues(cardsCounters).toLocaleString()}</span>{' '}
                        activities matching the current filter
                    </>
                }
                actions={
                    <>
                        <Button size={'large'} variant={'secondary'} onClick={closeModal}>
                            Cancel
                        </Button>

                        <Button size={'large'} variant={'primary'} destructive={true} onClick={handleConfirmModal}>
                            Rollback
                        </Button>
                    </>
                }
            >
                <div className={classes.bulkRollbackActionContent}>
                    <div className={classes.note}>(Note only Program Activities will be updated)</div>
                </div>
            </Modal>

            <ConfirmSaveModal
                isShowModal={confirmModal}
                onHideModal={() => setConfirmModal(false)}
                onConfirm={handleRollbackAction}
                actionButtonLabel={'Yes'}
            >
                Are you sure you want to delete {getObjectSumValues(cardsCounters).toLocaleString()} battlecards?
            </ConfirmSaveModal>
        </>
    );
};
